import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { updateMetaTags } from '../utils/seo';
import { siteConfigService } from '../services/api';
import DashboardOverview from './admin/DashboardOverview';
import ProductsManagement from './admin/ProductsManagement';
import OrdersManagement from './admin/OrdersManagement';
import InventoryManagement from './admin/InventoryManagement';
import DealersManagement from './admin/DealersManagement';
import DistributorsManagement from './admin/DistributorsManagement';
import InvestmentsManagement from './admin/InvestmentsManagement';
import CommissionsManagement from './admin/CommissionsManagement';
import JobsManagement from './admin/JobsManagement';
import BlogManagement from './admin/BlogManagement';
import CMSManagement from './admin/CMSManagement';
import CouponsManagement from './admin/CouponsManagement';
import InvoicesManagement from './admin/InvoicesManagement';
import ReportsManagement from './admin/ReportsManagement';
import NotificationsManagement from './admin/NotificationsManagement';
import AdsManagement from './admin/AdsManagement';
import NewsletterManagement from './admin/NewsletterManagement';
import SocialMediaManagement from './admin/SocialMediaManagement';
import MarketingSpotlight from './admin/MarketingSpotlight';
import ProductPromotions from './admin/ProductPromotions';
import UsersManagement from './admin/UsersManagement';
import SettingsManagement from './admin/SettingsManagement';
import SlidersManagement from './admin/SlidersManagement';
import MenuItemsManagement from './admin/MenuItemsManagement';
import PageSectionsManagement from './admin/PageSectionsManagement';
import FooterLinksManagement from './admin/FooterLinksManagement';
import PageBuilderManagement from './admin/PageBuilderManagement';
import ContactSubmissions from './admin/ContactSubmissions';
import ChannelManagement from './admin/ChannelManagement';

import {
  LayoutDashboard, Package, ShoppingCart, Users, BarChart3, Settings,
  Layers, Palette, Image, Menu, FileText, Link2, Megaphone, Bell,
  Newspaper, Percent, Receipt, Briefcase, TrendingUp, Truck,
  Store, Globe, LogOut, ChevronDown, ChevronRight, PanelLeftClose, PanelLeft,
  Shield, Coins, BookOpen, MapPin, Mail, FolderOpen
} from 'lucide-react';

import NavigationBuilder from './admin/NavigationBuilder';
import ModernPageBuilder from './admin/ModernPageBuilder';
import MediaLibrary from './admin/MediaLibrary';

const navGroups = [
  {
    label: 'Overview',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { id: 'reports', label: 'Reports & Analytics', icon: BarChart3 },
    ],
  },
  {
    label: 'Commerce',
    items: [
      { id: 'products', label: 'Products', icon: Package },
      { id: 'orders', label: 'Orders', icon: ShoppingCart },
      { id: 'inventory', label: 'Inventory', icon: Package },
      { id: 'coupons', label: 'Coupons', icon: Percent },
      { id: 'invoices', label: 'Invoices', icon: Receipt },
    ],
  },
  {
    label: 'Network',
    items: [
      { id: 'dealers', label: 'Dealers', icon: Store },
      { id: 'distributors', label: 'Distributors', icon: Truck },
      { id: 'channel', label: 'Channel Registrations', icon: Store },
      { id: 'investments', label: 'Investments', icon: TrendingUp },
      { id: 'commissions', label: 'Commissions', icon: Coins },
      { id: 'jobs', label: 'Careers', icon: Briefcase },
    ],
  },
  {
    label: 'Content & Design',
    items: [
      { id: 'modern-page-builder', label: 'Page Builder', icon: Palette },
      { id: 'media-library', label: 'Media Library', icon: FolderOpen },
      { id: 'navigation-builder', label: 'Navigation', icon: Menu },
      { id: 'cms', label: 'CMS (Pages)', icon: FileText },
      { id: 'sliders', label: 'Sliders', icon: Image },
      { id: 'sections', label: 'Page Sections', icon: Layers },
      { id: 'footer', label: 'Footer Links', icon: Link2 },
      { id: 'blogs', label: 'Blog', icon: BookOpen },
      { id: 'newsletters', label: 'Newsletters', icon: Newspaper },
      { id: 'ads', label: 'Ads', icon: Megaphone },
      { id: 'social', label: 'Social Media', icon: Globe },
    ],
  },
  {
    label: 'Marketing & Engagement',
    items: [
      { id: 'promotions', label: 'Product Promotions', icon: Percent },
      { id: 'spotlights', label: 'Local Spotlight', icon: MapPin },
      { id: 'newsletters', label: 'Newsletters', icon: Newspaper },
      { id: 'ads', label: 'Ads Management', icon: Megaphone },
      { id: 'social', label: 'Social Integration', icon: Globe },
    ],
  },
  {
    label: 'System',
    items: [
      { id: 'users', label: 'Users', icon: Users },
      { id: 'notifications', label: 'Notifications', icon: Bell },
      { id: 'contact-submissions', label: 'Contact Submissions', icon: Mail },
      { id: 'settings', label: 'Settings', icon: Settings },
    ],
  },
];

function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    console.log('Admin User Context:', {
      name: user?.name,
      roles: user?.roles?.map(r => r.slug),
      permissions: user?.permissions
    });

    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }

    const loadSettings = async () => {
      try {
        const settingsData = await siteConfigService.settings();
        updateMetaTags({
          title: 'Admin Dashboard',
          siteName: settingsData?.site_name || 'Ortus FMCG',
          favicon: settingsData?.site_favicon
        });
      } catch (error) {
        updateMetaTags({
          title: 'Admin Dashboard',
          siteName: 'Ortus FMCG'
        });
      }
    };

    loadSettings();
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleGroup = (label) => {
    setCollapsedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const activeLabel = navGroups
    .flatMap((g) => g.items)
    .find((i) => i.id === activeTab)?.label || 'Dashboard';

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardOverview />;
      case 'products':
        return <ProductsManagement />;
      case 'cms':
        return <CMSManagement />;
      case 'orders':
        return <OrdersManagement />;
      case 'inventory':
        return <InventoryManagement />;
      case 'dealers':
        return <DealersManagement />;
      case 'distributors':
        return <DistributorsManagement />;
      case 'investments':
        return <InvestmentsManagement />;
      case 'commissions':
        return <CommissionsManagement />;
      case 'jobs':
        return <JobsManagement />;
      case 'blogs':
        return <BlogManagement />;
      case 'coupons':
        return <CouponsManagement />;
      case 'invoices':
        return <InvoicesManagement />;
      case 'reports':
        return <ReportsManagement />;
      case 'notifications':
        return <NotificationsManagement />;
      case 'ads':
        return <AdsManagement />;
      case 'newsletters':
        return <NewsletterManagement />;
      case 'social':
        return <SocialMediaManagement />;
      case 'spotlights':
        return <MarketingSpotlight />;
      case 'promotions':
        return <ProductPromotions />;
      case 'users':
        return <UsersManagement />;
      case 'settings':
        return <SettingsManagement />;
      case 'sliders':
        return <SlidersManagement />;
      case 'navigation-builder':
        return <NavigationBuilder />;
      case 'sections':
        return <PageSectionsManagement />;
      case 'footer':
        return <FooterLinksManagement />;
      case 'page-builder':
        return <PageBuilderManagement />;
      case 'modern-page-builder':
        return <ModernPageBuilder />;
      case 'media-library':
        return <MediaLibrary />;
      case 'contact-submissions':
        return <ContactSubmissions />;
      case 'channel':
        return <ChannelManagement />;
      default:
        return <DashboardOverview />;
    }
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-6 flex items-center gap-3 border-b border-slate-800/50">
        <div className="w-9 h-9 rounded-lg bg-red-600 flex items-center justify-center text-white font-black text-lg shrink-0">O</div>
        <div>
          <h1 className="text-white font-bold text-sm leading-tight">Ortus Admin</h1>
          <p className="text-slate-400 text-[10px] uppercase tracking-wider">FMCG Platform</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-3 px-3 space-y-1">
        {navGroups.map((group) => {
          const collapsed = collapsedGroups[group.label];
          return (
            <div key={group.label}>
              <button
                onClick={() => toggleGroup(group.label)}
                className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold hover:text-slate-300 transition"
              >
                {group.label}
                {collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
              </button>
              {!collapsed && (
                <div className="space-y-0.5 mt-1">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => {
                          setActiveTab(item.id);
                          setMobileMenuOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition group ${
                          isActive
                            ? 'bg-red-600/20 text-red-400 font-medium border border-red-600/30'
                            : 'text-slate-400 hover:bg-slate-800/60 hover:text-slate-200'
                        }`}
                      >
                        <Icon size={16} className={isActive ? 'text-red-400' : 'text-slate-500 group-hover:text-slate-300'} />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="p-3 border-t border-slate-800/50 space-y-2">
        <div className="px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-red-600/20 text-red-400 flex items-center justify-center text-xs font-bold">
              {user?.name?.charAt(0) || 'A'}
            </div>
            <div className="min-w-0">
              <p className="text-slate-200 text-xs font-medium truncate">{user?.name || 'Admin'}</p>
              <p className="text-slate-500 text-[10px] truncate">{user?.email || 'admin@ortus.com'}</p>
            </div>
          </div>
        </div>
        <Link to="/" className="flex items-center gap-2 px-3 py-2 rounded-lg text-slate-400 hover:bg-slate-800/60 hover:text-slate-200 text-sm transition">
          <Globe size={14} /> View Site
        </Link>
        <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-red-400 hover:bg-red-600/10 hover:text-red-300 text-sm transition">
          <LogOut size={14} /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="admin-panel min-h-screen bg-slate-950 text-slate-200 flex">
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setMobileMenuOpen(false)} />
      )}

      <aside className={`hidden lg:flex flex-col bg-slate-900 border-r border-slate-800/60 transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-0 overflow-hidden'}`}>
        <SidebarContent />
      </aside>

      {mobileMenuOpen && (
        <aside className="fixed inset-y-0 left-0 w-64 bg-slate-900 border-r border-slate-800/60 z-50 lg:hidden">
          <SidebarContent />
        </aside>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 bg-slate-900/80 backdrop-blur border-b border-slate-800/60 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileMenuOpen(true)} className="lg:hidden p-1.5 rounded-lg hover:bg-slate-800 text-slate-400">
              <PanelLeft size={20} />
            </button>
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="hidden lg:flex p-1.5 rounded-lg hover:bg-slate-800 text-slate-400">
              {sidebarOpen ? <PanelLeftClose size={18} /> : <PanelLeft size={18} />}
            </button>
            <div className="h-5 w-px bg-slate-700 hidden lg:block" />
            <div>
              <h2 className="text-sm font-semibold text-slate-100">{activeLabel}</h2>
              <p className="text-[10px] text-slate-500">Ortus Admin Panel v1.0.3</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-600/10 border border-red-600/20 text-red-400 text-xs font-medium">
              <Shield size={12} /> Admin
            </span>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}

export default AdminDashboard;
